import type { WorkflowStep, WorkflowTemplate } from './templateTypes';

/**
 * The identity half of a branched application: the fields BMO keys the application on,
 * all of which stay the BASE form's even after a swap.
 *
 * `id` is what reaches BMO as `plctData.smartforms.formId`, `appName` as
 * `appInfo.appType`, and `nameEn` as `plctData.smartforms.formName`. Keeping them fixed is
 * what makes a branch one application with two bodies of content rather than two
 * applications — a redirect, which really does start a second application, is the other
 * shape and lives in `optionRedirect.ts`.
 *
 * Everything NOT listed here comes from the branch target, including both version labels:
 * the version describes the content the advisor actually filled, which is what the PDFs
 * and the e-signature package were built from.
 *
 * `variables` is the one entry here that is not identity in the BMO sense, and it is kept for
 * a related reason: workflow variables are CONFIGURATION OF THE APPLICATION, not authored
 * content of the form. The advisor opened the base form, so the base form's configuration is
 * what this application runs under — a `variable.fund` binding on a swapped-in form resolves
 * against the ORIGINAL workflow's value, not the target's own same-named variable. Letting the
 * target's win would mean the fund an application files under could change out from under the
 * advisor purely because a branch fired.
 */
function withBaseIdentity(base: WorkflowTemplate, target: WorkflowTemplate) {
  return {
    ...target,
    id: base.id,
    appName: base.appName,
    appNameFr: base.appNameFr,
    nameEn: base.nameEn,
    nameFr: base.nameFr,
    descriptionEn: base.descriptionEn,
    descriptionFr: base.descriptionFr,
    variables: base.variables,
  };
}

/** The target's first DynamicForm step — the one `appendFirstStep` merges in. Null when
 *  the target has no form steps at all, which makes the toggle a no-op rather than an
 *  error: an originator can tick it before the target has been built out. */
function firstDynamicStep(target: WorkflowTemplate): WorkflowStep | null {
  return target.steps.find((s) => s.type === 'DynamicForm') ?? null;
}

/**
 * What the advisor navigates once a branch fires: the base form's steps up to and
 * including the one carrying the rule, then the target's steps.
 *
 * With `appendFirstStep`, the target's first DynamicForm step's SECTIONS are appended to
 * the branch step instead, so the branch opens with its first questions on the page the
 * advisor is already looking at, and that step does not also arrive on its own.
 *
 * Note what is NOT carried across: the base form's steps below the branch point are gone,
 * and with them their PDF entries, e-signature config and upload entries. That is the
 * whole point — after the swap this is the target's form, wearing the base form's name.
 */
export function composeBranchedTemplate(
  base: WorkflowTemplate,
  target: WorkflowTemplate,
  branchStepIndex: number,
  appendFirstStep: boolean,
): WorkflowTemplate {
  const prefix = base.steps.slice(0, branchStepIndex);
  const branchStep = base.steps[branchStepIndex];
  const merged = appendFirstStep ? firstDynamicStep(target) : null;

  const ownStep: WorkflowStep[] = branchStep
    ? [
        merged
          ? { ...branchStep, sections: [...branchStep.sections, ...merged.sections] }
          : branchStep,
      ]
    : [];

  // Identity is matched by id, not by reference: `target` may have been re-fetched since
  // `merged` was picked, and the two steps would then not be the same object.
  const tail = merged ? target.steps.filter((s) => s.id !== merged.id) : target.steps;

  return { ...withBaseIdentity(base, target), steps: [...prefix, ...ownStep, ...tail] };
}

/**
 * What ships to BMO: the same identity override over the target's OWN, unmerged step list.
 *
 * Only the selected form's answers are persisted — the base form's retained steps are not
 * sent — so the snapshot must not walk the prefix. Building it from this template rather
 * than from the navigable one is the whole of that rule; `buildApplicationSnapshot` visits
 * only the steps it is handed and needs no knowledge of branching.
 *
 * Using the target's unmerged steps also handles `appendFirstStep` correctly for free: the
 * merged step's base-form sections are not here, so their answers drop out, while the
 * target's own first step is present exactly once.
 */
export function composeBranchedPayloadTemplate(
  base: WorkflowTemplate,
  target: WorkflowTemplate,
): WorkflowTemplate {
  return { ...withBaseIdentity(base, target), steps: target.steps };
}

/** The field and section ids a template owns — see {@link templateOwnedIds}. */
export type TemplateOwnedIds = {
  fieldIds: readonly string[];
  sectionIds: readonly string[];
};

/**
 * Every field and section id a template owns, flat.
 *
 * Run over the BRANCH TARGET, this is the set of answers belonging to the branch rather
 * than to the base form — which is what the runtime parks when the advisor swaps away.
 * Taking it from the target rather than from the composed template is what makes
 * `appendFirstStep` work: the merged sections came from the target, so their fields count
 * as the branch's even though they render inside a base step.
 *
 * Sections are collected as well as fields because a repeatable section contributes a key
 * of its own (`__instanceCount:<sectionId>`) that no field id would match.
 */
export function templateOwnedIds(template: WorkflowTemplate): TemplateOwnedIds {
  const fieldIds: string[] = [];
  const sectionIds: string[] = [];
  for (const step of template.steps)
    for (const section of step.sections ?? []) {
      sectionIds.push(section.id);
      for (const row of section.rows ?? [])
        for (const col of row.columns ?? []) if (col.field) fieldIds.push(col.field.id);
    }
  return { fieldIds, sectionIds };
}

const INSTANCE_KEY = /^inst:([^:]+):\d+:(.+)$/;
const INSTANCE_COUNT_KEY = /^__instanceCount:(.+)$/;

/**
 * Does this `values` key belong to the given template?
 *
 * Plain answers are keyed by field id, but a repeatable section's are keyed
 * `inst:<sectionId>:<n>:<fieldId>` with a sibling `__instanceCount:<sectionId>` — so a
 * membership test that only compared field ids would leave every repeated answer behind
 * when the advisor swapped forms, and leave the instance count behind to seed the next
 * form with the wrong number of rows.
 */
export function ownsValueKey(owned: TemplateOwnedIds, key: string): boolean {
  const instance = INSTANCE_KEY.exec(key);
  if (instance) return owned.fieldIds.includes(instance[2]!);
  const count = INSTANCE_COUNT_KEY.exec(key);
  if (count) return owned.sectionIds.includes(count[1]!);
  return owned.fieldIds.includes(key);
}
